/**
 * Phase 6 cross-cutting PR 4e — pure aggregation helpers over a season's
 * training_plans + plan_days.
 *
 * Backs the last two unwired proactive triggers (`streak_milestone` and
 * `compliance_drop`) by reducing the multi-week plan_days dataset to:
 *   - `currentStreak`: consecutive completed non-rest training days
 *     ending at the most recent week (rest days are transparent).
 *   - `{ thisWeek, lastWeek }` compliance %: completed / training-days
 *     for the current and previous week_number.
 *
 * Both detectors accept these computed values via
 * `ProactiveDetectorInput` (already shipped in PR 4 — this PR just
 * populates the fields that were placeholder `null` / `0`).
 *
 * **Vanilla parity — one deliberate divergence:**
 *
 * Vanilla `proactive-checks.js:118-123` flattens plans in
 * `state.seasonPlans` array order (week_number ASC from the API),
 * then within each plan sorts `plan_days` by `day_of_week` DESC. That
 * walk measures "longest opening streak from start of season" rather
 * than "current streak ending today." For an athlete who's been
 * perfectly consistent since week 1 the two agree, but they diverge
 * the moment any historical week has a missed non-rest day.
 *
 * `computeCurrentStreak` corrects this to walk week_number DESC first,
 * then day_of_week DESC, which matches the user expectation of "12-day
 * current streak" surfaced in the dashboard. Documented as a
 * justified deviation in `OVERRIDES.md` (D5 — vanilla streak
 * computation re-anchored to "ending today" semantics).
 */

/** Minimal plan-day shape — both helpers ignore other columns. */
export interface PlanDayShape {
  /** Monday-based 0..6 (per `weekdayUtils.ts` convention). */
  day_of_week: number;
  /** Free-form workout label; `null`/empty/contains "rest" → rest day. */
  workout_type: string | null;
  /** 'completed' | 'skipped' | 'upcoming' | null. */
  completion_status: string | null;
  /** Free-form workout details ("4 mi easy", "Cross-training (swimming,
   *  20-30 minutes)"). Optional — only fetched by consumers that derive
   *  per-day metrics (Backlog J season calendar via `deriveDayMetric`). */
  details?: string | null;
}

/** Minimal plan shape — both helpers ignore other columns. */
export interface SeasonPlanShape {
  /** Sequential 1-based week number (matches macroPlan.weekNumber). */
  week_number: number;
  /** May be `null` when the relation came back empty. */
  plan_days: PlanDayShape[] | null;
}

/**
 * Season-model Phase 1 — collapse duplicate week rows to the NEWEST per
 * `week_number`. The chat-coach path historically stacked multiple
 * `training_plans` rows for the same week (week_number was a running count,
 * not the real week), which made streak + compliance count the same week
 * 2–6×. This keeps ONE row per week (latest `created_at` wins) so the
 * aggregation below is correct even on legacy data. Returns week_number ASC.
 *
 * The write-side fix (replace-on-write) + the UNIQUE(season_id, week_number)
 * constraint prevent NEW duplicates; this guards reads against existing ones.
 */
export function dedupePlansByWeekNewest<
  T extends { week_number: number; created_at?: string | null },
>(rows: T[]): T[] {
  const byWeek = new Map<number, T>();
  for (const r of rows) {
    const existing = byWeek.get(r.week_number);
    if (!existing || (r.created_at ?? '') > (existing.created_at ?? '')) {
      byWeek.set(r.week_number, r);
    }
  }
  return [...byWeek.values()].sort((a, b) => a.week_number - b.week_number);
}

/**
 * Season-model Phase 3 (progressive week disclosure) — the SINGLE invariant
 * for "which weeks are DRAFTED." A macro plan reserves every week of the
 * season, but the coach only drafts day-by-day detail (`training_plans` +
 * `plan_days`) for a few. A week counts as drafted ONLY when it has a real
 * materialized plan — i.e. at least one `plan_day`. This matches the semantics
 * `useTrainingPlan` / `plan-week.tsx` already use (a row that exists but yields
 * an empty table is treated as NOT built, via `dbToPlanTable` → null).
 *
 * Pass already-deduped rows (newest-per-week) — callers run
 * `dedupePlansByWeekNewest` first (the write-side UNIQUE(season_id,
 * week_number) constraint means there's normally one row per week anyway; the
 * dedup guards legacy data). Both the `useDraftedWeeks` hook AND the Today page
 * (which already loads `useSeasonPlans`) funnel through THIS helper so the
 * drafted-vs-undrafted decision can never diverge between surfaces.
 */
export function draftedWeeksFromPlans(plans: SeasonPlanShape[]): Set<number> {
  const drafted = new Set<number>();
  for (const p of plans) {
    if (Array.isArray(p.plan_days) && p.plan_days.length > 0) {
      drafted.add(p.week_number);
    }
  }
  return drafted;
}

/** A day is a "rest day" when its workout_type contains the word
 *  "rest" (case-insensitive). Matches vanilla
 *  `proactive-checks.js:71` and `:122`. */
function isRestDay(workoutType: string | null | undefined): boolean {
  return (workoutType ?? '').toLowerCase().includes('rest');
}

/**
 * Per-week compliance: percentage of NON-REST plan_days marked
 * `completion_status === 'completed'`, rounded to integer.
 *
 * Returns `null` when the week has no training days (e.g., a full
 * deload / rest week) — the compliance-drop detector treats `null` as
 * "unknown" and won't fire on it. Matches vanilla's `training.length
 * ? Math.round(...) : 100` but uses `null` instead of `100` so we
 * never accidentally fire compliance_drop because the prior week was
 * a deload.
 *
 * Vanilla-parity DEVIATION: vanilla returns 100 on empty-training
 * weeks. We return null so deload weeks don't satisfy
 * "lastWeek > 70" by accident. Both detectors guard on `!== null`
 * already, so this is the safer default.
 */
export function computeWeekCompliancePct(planDays: PlanDayShape[]): number | null {
  const training = planDays.filter((d) => !isRestDay(d.workout_type));
  if (training.length === 0) return null;
  const completed = training.filter((d) => d.completion_status === 'completed').length;
  return Math.round((completed / training.length) * 100);
}

/**
 * Resolve compliance for this week and last week given the full
 * season plan list and the current week_number derived from the
 * macro plan / calendar (`phaseFromMacroPlan(macroPlanWeeks).weekNumber`).
 *
 * Returns `{ thisWeek: null, lastWeek: null }` when:
 *   - `currentWeekNumber` is null (macro plan not loaded / season
 *     hasn't started)
 *   - the matching plan row doesn't exist for that week (plan
 *     generation hasn't caught up)
 *   - the week has no training days (full rest week)
 *
 * The detector short-circuits on either being null, so any of these
 * states is a safe no-fire.
 */
export function computeThisAndLastWeekCompliance(
  plans: SeasonPlanShape[],
  currentWeekNumber: number | null,
): { thisWeek: number | null; lastWeek: number | null } {
  if (currentWeekNumber === null) return { thisWeek: null, lastWeek: null };
  const thisPlan = plans.find((p) => p.week_number === currentWeekNumber);
  const lastPlan = plans.find((p) => p.week_number === currentWeekNumber - 1);
  return {
    thisWeek: thisPlan ? computeWeekCompliancePct(thisPlan.plan_days ?? []) : null,
    lastWeek: lastPlan ? computeWeekCompliancePct(lastPlan.plan_days ?? []) : null,
  };
}

/**
 * Current consecutive-completed-training-day streak.
 *
 * Walk order (vanilla DEVIATION — see file-level docstring): plans
 * by `week_number` DESC, then within each plan by `day_of_week` DESC.
 * That puts the latest day of the latest week first, so the streak
 * counts "consecutive completions ending at the most recent day."
 *
 * Walk rules (port of vanilla `proactive-checks.js:120-123`):
 *   - `completion_status === 'completed'` → increment streak
 *   - workout_type doesn't include "rest" AND not completed → break
 *   - rest day → continue WITHOUT incrementing (rest days are
 *     transparent; they don't reset the streak)
 *
 * Returns 0 when the input is empty or when the most-recent
 * non-rest day isn't completed. The streak_milestone detector only
 * fires when this value hits 7 / 14 / 21 / 30 / 50 / 100, so 0 is a
 * safe default.
 *
 * B-25 — the walk must start at TODAY, not at the latest drafted day.
 * Without the clamp, any day that hasn't happened yet (the rest of the
 * current week, or a whole batch-drafted future week) is the first day
 * the walk visits — non-rest + not-completed → instant break → the
 * streak read 0 for every drafted-ahead athlete no matter how many
 * consecutive days they'd completed (founder live report 2026-07-19:
 * Sat+Sun completed, streak showed "Start your streak"). Pass the
 * resolved `currentWeekNumber` + Monday-based `todayDayOfWeek` (0=Mon)
 * to clamp the walk to days that have actually occurred; omit them
 * (or pass null) for the legacy unclamped walk.
 */
export function computeCurrentStreak(
  plans: SeasonPlanShape[],
  currentWeekNumber?: number | null,
  todayDayOfWeek?: number | null,
): number {
  const clampWeek = typeof currentWeekNumber === 'number' ? currentWeekNumber : null;
  const clampDay = typeof todayDayOfWeek === 'number' ? todayDayOfWeek : null;
  const orderedPlans = [...plans]
    .filter((p) => clampWeek === null || p.week_number <= clampWeek)
    .sort((a, b) => b.week_number - a.week_number);
  let streak = 0;
  for (const plan of orderedPlans) {
    const days = [...(plan.plan_days ?? [])]
      // Days after today in the CURRENT week haven't happened — they can
      // neither extend nor break the streak. TODAY is transparent until
      // completed (review amendment): a pending 7am today must not zero
      // yesterday's streak, and StreakChip / ruleStreakProtection /
      // streak_milestone all read this value in exactly that pre-workout
      // window. Past weeks walk in full.
      .filter(
        (d) =>
          clampWeek === null ||
          clampDay === null ||
          plan.week_number !== clampWeek ||
          d.day_of_week < clampDay ||
          (d.day_of_week === clampDay && d.completion_status === 'completed'),
      )
      .sort((a, b) => b.day_of_week - a.day_of_week);
    for (const d of days) {
      if (d.completion_status === 'completed') {
        streak += 1;
      } else if (!isRestDay(d.workout_type)) {
        return streak;
      }
      // else: rest day — transparent, continue without incrementing.
    }
  }
  return streak;
}

// ─── SR-2 — season recap (pure) ──────────────────────────────────────

/** Minimal race shape — structural so this lib stays decoupled from
 *  `racePR.ts` (`RaceResult` satisfies it). */
export interface RecapRaceShape {
  /** 'YYYY-MM-DD' (timestamps tolerated — sliced to the date). */
  race_date: string;
  is_pr: boolean;
  /** Nullable FK — legacy rows are often unstamped. */
  season_id: string | null;
}

export interface SeasonRecap {
  /** Non-rest plan_days marked completed, across the whole season. */
  sessionsCompleted: number;
  /** All non-rest plan_days across the whole season. */
  sessionsTotal: number;
  /** Rounded completed/total %; null when the season had no training days. */
  compliancePct: number | null;
  /** Weeks with at least one plan_day (drafted). */
  weeksDrafted: number;
  /** Races run during the season (see inclusion rule below). */
  racesInSeason: number;
  /** Of those, how many are standing PRs. */
  prCount: number;
}

/**
 * Reduce a finished season to honest, data-backed recap stats for the
 * after_season `/plan` surface ("Review how it went, then start your next
 * block").
 *
 * Race inclusion is a UNION: a race counts when it's stamped with this
 * season's id OR (both season dates known AND race_date falls inside
 * [start_date, end_date]) — the date-window arm covers legacy rows that
 * were never stamped, the id arm covers stamped rows even if the season's
 * dates were later shifted. Each race counts once.
 *
 * Input plans are deduped to newest-per-week first (same guard as the
 * other aggregations — legacy data can hold duplicate week rows).
 */
export function computeSeasonRecap(
  plans: SeasonPlanShape[],
  races: RecapRaceShape[] | null,
  season: { id: string; start_date: string | null; end_date: string | null },
): SeasonRecap {
  const deduped = dedupePlansByWeekNewest(plans);

  let sessionsTotal = 0;
  let sessionsCompleted = 0;
  for (const plan of deduped) {
    for (const d of plan.plan_days ?? []) {
      if (isRestDay(d.workout_type)) continue;
      sessionsTotal += 1;
      if (d.completion_status === 'completed') sessionsCompleted += 1;
    }
  }
  const compliancePct =
    sessionsTotal > 0 ? Math.round((sessionsCompleted / sessionsTotal) * 100) : null;

  const start = String(season.start_date ?? '').slice(0, 10);
  const end = String(season.end_date ?? '').slice(0, 10);
  const hasWindow = start.length === 10 && end.length === 10;
  const inSeason = (races ?? []).filter((r) => {
    if (r.season_id != null && r.season_id === season.id) return true;
    const day = String(r.race_date ?? '').slice(0, 10);
    return hasWindow && day.length === 10 && day >= start && day <= end;
  });

  return {
    sessionsCompleted,
    sessionsTotal,
    compliancePct,
    weeksDrafted: draftedWeeksFromPlans(deduped).size,
    racesInSeason: inSeason.length,
    prCount: inSeason.filter((r) => r.is_pr === true).length,
  };
}

// Exported for unit tests.
export const __testing__ = { isRestDay };
